import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import papi, { formatErr } from "@/lib/partnerApi";
import PartnerNav from "@/pages/partner/PartnerNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, IndianRupee, ShoppingBag, Download } from "lucide-react";

const empty = { name: "", category: "advisory", amount: "", price_label: "", price_note: "", validity_label: "", validity_days: "", best_for: "", billing: "one_time", buyable: true, cta: "Buy Now", action: "buy", popular: false, order: 99, short_desc: "", features: "", active: true };

export default function AdminPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, r] = await Promise.all([papi.get("/admin/plans"), papi.get("/admin/plans/revenue")]);
      setPlans(p.data); setRevenue(r.data);
    } catch (e) { if (e.response?.status === 401) navigate("/login"); else toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...empty, ...p, amount: p.amount ?? "", validity_days: p.validity_days ?? "", features: (p.features || []).join("\n") });
    setEditId(p.id); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      amount: form.amount === "" ? null : Number(form.amount),
      validity_days: form.validity_days === "" ? null : Number(form.validity_days),
      order: Number(form.order) || 99,
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editId) await papi.put(`/admin/plans/${editId}`, payload);
      else await papi.post("/admin/plans", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try { await papi.delete(`/admin/plans/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const csvUrl = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    papi.get("/admin/plans/transactions.csv", { responseType: "blob" }).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "plan_transactions.csv"; a.click(); window.URL.revokeObjectURL(url);
    }).catch(() => toast.error("Export failed"));
  };

  if (plans === null) return <div className="min-h-screen bg-[#FBFAF9]"><PartnerNav /><div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div></div>;

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <PartnerNav actions={<Button onClick={openNew} data-testid="admin-new-plan" className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl"><Plus className="h-4 w-4 mr-1.5" /> New Plan</Button>} />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-display text-2xl font-extrabold text-slate-900 mb-6">Advisory Plans & Revenue</h1>

        {/* Revenue */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="card-premium rounded-2xl p-5"><div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center mb-3"><IndianRupee className="h-5 w-5 text-emerald-600" /></div><p className="font-display text-2xl font-extrabold text-slate-900">₹{(revenue?.total_revenue || 0).toLocaleString("en-IN")}</p><p className="text-xs text-slate-500 mt-1">Total plan revenue</p></div>
          <div className="card-premium rounded-2xl p-5"><div className="h-9 w-9 rounded-lg bg-[#0E9AC8]/10 flex items-center justify-center mb-3"><ShoppingBag className="h-5 w-5 text-[#0E9AC8]" /></div><p className="font-display text-2xl font-extrabold text-slate-900">{revenue?.orders || 0}</p><p className="text-xs text-slate-500 mt-1">Paid orders</p></div>
          <div className="card-premium rounded-2xl p-5 flex flex-col justify-between"><p className="text-sm font-semibold text-slate-700">Export</p><Button onClick={csvUrl} variant="outline" data-testid="admin-export-csv" className="rounded-xl mt-3 w-fit"><Download className="h-4 w-4 mr-1.5" /> Transactions CSV</Button></div>
        </div>

        {/* Plans table */}
        <div className="card-premium rounded-2xl overflow-hidden" data-testid="admin-plans-table">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr><th className="text-left px-5 py-3 font-semibold">Plan</th><th className="text-left px-3 py-3 font-semibold">Price</th><th className="text-left px-3 py-3 font-semibold">Validity</th><th className="text-left px-3 py-3 font-semibold">Buyable</th><th className="text-left px-3 py-3 font-semibold">Active</th><th className="px-3 py-3"></th></tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p.id} data-testid={`admin-plan-${p.id}`} className="border-t border-slate-100">
                    <td className="px-5 py-3 font-medium text-slate-800">{p.name} {p.popular && <span className="ml-1 text-[10px] font-bold text-[#0E9AC8]">POPULAR</span>}<div className="text-xs text-slate-400 font-normal">{p.category}</div></td>
                    <td className="px-3 py-3 text-slate-600">{p.price_label}{p.amount ? ` · ₹${p.amount.toLocaleString("en-IN")}` : ""}</td>
                    <td className="px-3 py-3 text-slate-600">{p.validity_label}</td>
                    <td className="px-3 py-3">{p.buyable ? "Yes" : "No"}</td>
                    <td className="px-3 py-3">{p.active ? <span className="text-emerald-600 font-semibold">Live</span> : <span className="text-slate-400">Hidden</span>}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="text-slate-500"><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editId ? "Edit Plan" : "New Plan"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <F label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" /></F>
            <F label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl" placeholder="advisory / consultation / addon_dpm" /></F>
            <F label="Price label"><Input value={form.price_label} onChange={(e) => setForm({ ...form, price_label: e.target.value })} className="rounded-xl" placeholder="₹4,999 / Free / Custom" /></F>
            <F label="Amount (₹, for Buy Now)"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl" /></F>
            <F label="Price note"><Input value={form.price_note} onChange={(e) => setForm({ ...form, price_note: e.target.value })} className="rounded-xl" /></F>
            <F label="Best for"><Input value={form.best_for} onChange={(e) => setForm({ ...form, best_for: e.target.value })} className="rounded-xl" /></F>
            <F label="Validity label"><Input value={form.validity_label} onChange={(e) => setForm({ ...form, validity_label: e.target.value })} className="rounded-xl" placeholder="30 Days" /></F>
            <F label="Validity days"><Input type="number" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} className="rounded-xl" /></F>
            <F label="Billing"><Input value={form.billing} onChange={(e) => setForm({ ...form, billing: e.target.value })} className="rounded-xl" placeholder="one_time / monthly / annual / free / custom" /></F>
            <F label="CTA label"><Input value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} className="rounded-xl" /></F>
            <F label="Action"><Input value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className="rounded-xl" placeholder="buy / consult / start / contact" /></F>
            <F label="Order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="rounded-xl" /></F>
            <div className="sm:col-span-2"><F label="Short description"><Input value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} className="rounded-xl" /></F></div>
            <div className="sm:col-span-2"><F label="Features (one per line)"><Textarea rows={5} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="rounded-xl" /></F></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.buyable} onChange={(e) => setForm({ ...form, buyable: e.target.checked })} /> Buyable</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.popular} onChange={(e) => setForm({ ...form, popular: e.target.checked })} /> Popular</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active (visible)</label>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="admin-save-plan" className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Plan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1.5">{children}</div></div>;
